import { z } from 'zod';
import {
  protocolTypeSchema,
  vehicleCategorySchema,
  fuelTypeSchema,
  signatureRoleSchema,
  ProtocolType,
} from './enums';
import { checklistAnswerRecordSchema } from './checklists';
import { damageMarkerSchema } from './damage';
import { photoMetaSchema } from './photos';

/** Kennzeichen normalisieren: "f-ap 5486e" -> "F-AP 5486E" */
export function normalizePlate(input: string): string {
  const cleaned = input.toUpperCase().replace(/\s+/g, ' ').trim();
  const m = cleaned.match(/^([A-ZÄÖÜ]{1,3})[\s-]*([A-Z]{1,2})[\s-]*(\d{1,4}\s?[EH]?)$/);
  if (!m) return cleaned;
  return `${m[1]}-${m[2]} ${m[3]!.replace(/\s+/g, '')}`;
}

export const plateSchema = z
  .string()
  .min(4)
  .max(15)
  .transform(normalizePlate)
  .refine((v) => /^[A-ZÄÖÜ]{1,3}-[A-Z]{1,2} ?\d{1,4}[EH]?$/.test(v) || v.length >= 4, {
    message: 'Kennzeichen ungültig',
  });

/** FIN: 17 Zeichen, ohne I, O, Q. */
export const vinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-HJ-NPR-Z0-9]{17}$/, 'FIN muss 17 Zeichen haben (ohne I, O, Q)');

export const personSchema = z.object({
  lastName: z.string().min(1).max(120),
  firstName: z.string().max(120).optional(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(60).optional(),
  personnelNumber: z.string().max(40).optional(),
  companyName: z.string().max(200).optional(),
  costCenter: z.string().max(40).optional(),
  street: z.string().max(200).optional(),
  zip: z.string().max(12).optional(),
  city: z.string().max(120).optional(),
  driversLicenseNo: z.string().max(60).optional(),
  supervisorEmail: z.string().email().optional().or(z.literal('')),
  budgetGroup: z.string().max(40).optional(),
});
export type PersonInput = z.infer<typeof personSchema>;

export const signatureInputSchema = z.object({
  clientId: z.string().min(1).max(64),
  role: signatureRoleSchema,
  /** Name in Druckbuchstaben, wie auf dem Papierformular gefordert. */
  name: z.string().min(2).max(160),
  signedAt: z.string().datetime().optional(),
  place: z.string().max(120).optional(),
});

/** Erste Taetigkeitsstaette - steuerlich relevant, aus dem XLSX-Formular. */
export const WorkplaceKind = {
  ERSTE_TAETIGKEITSSTAETTE: 'ERSTE_TAETIGKEITSSTAETTE',
  KEINE_ERSTE_TAETIGKEITSSTAETTE: 'KEINE_ERSTE_TAETIGKEITSSTAETTE',
} as const;
export type WorkplaceKind = (typeof WorkplaceKind)[keyof typeof WorkplaceKind];

/** Vorgangsspezifische Nutzdaten. */
export const nutzerwechselPayloadSchema = z.object({
  previousUser: personSchema.optional(),
  newUser: personSchema.optional(),
  privateUse: z.boolean(),
  workplaceKind: z.nativeEnum(WorkplaceKind).optional(),
  workplaceReason: z.string().max(1000).optional(),
  fuelCardNumber: z.string().max(60).optional(),
  objectId: z.string().max(60).optional(),
  /** Was passiert im Anschluss? Steuert die Folgevorgaenge in der App. */
  hasReturn: z.boolean(),
  hasHandover: z.boolean(),
});

export const uebergabePayloadSchema = z.object({
  newUser: personSchema,
  interiorSmell: z.string().max(500).optional(),
  policyAcknowledged: z.boolean().default(true),
});

export const rueckgabePayloadSchema = z.object({
  previousUser: personSchema,
  interiorSmell: z.string().max(500).optional(),
});

export const poolAusgabePayloadSchema = z.object({
  driverName: z.string().min(2).max(160),
  driverEmail: z.string().email().optional().or(z.literal('')),
  driverPhone: z.string().max(60).optional(),
  purpose: z.string().max(200).optional(),
  expectedReturnAt: z.string().datetime().optional(),
});

export const poolRuecknahmePayloadSchema = z.object({
  /** clientUuid oder Server-ID des zugehoerigen Ausgabe-Vorgangs. */
  tripRef: z.string().max(64).optional(),
  driverName: z.string().min(2).max(160),
  refuelled: z.boolean().optional(),
  cleaned: z.boolean().optional(),
  newDamage: z.boolean().default(false),
});

export const zustandscheckPayloadSchema = z.object({
  conditionAnswers: z.record(z.string(), z.boolean()),
  defects: z.string().max(2000).optional(),
  fuelLevelPercent: z.number().int().min(0).max(100).optional(),
  nextInspectionDue: z.string().max(20).optional(),
});

export const protocolPayloadSchema = z.union([
  nutzerwechselPayloadSchema,
  uebergabePayloadSchema,
  rueckgabePayloadSchema,
  poolAusgabePayloadSchema,
  poolRuecknahmePayloadSchema,
  zustandscheckPayloadSchema,
  z.object({}),
]);

/** Was die App beim Sync hochlaedt (Fotodateien folgen separat als Multipart). */
export const protocolSyncSchema = z.object({
  clientUuid: z.string().uuid(),
  type: protocolTypeSchema,
  /** Verknuepft Rueckgabe + Uebergabe eines Nutzerwechsels zu einem Vorgang. */
  groupClientUuid: z.string().uuid().optional(),

  vehicleId: z.string().optional(),
  plate: z.string().min(2).max(15),
  vin: z.string().max(17).optional(),
  make: z.string().max(80).optional(),
  model: z.string().max(80).optional(),
  category: vehicleCategorySchema,
  fuelType: fuelTypeSchema.optional(),

  occurredAt: z.string().datetime(),
  mileage: z.number().int().min(0).max(2_000_000).optional(),
  place: z.string().max(160).optional(),

  lat: z.number().min(-90).max(90).optional().nullable(),
  lng: z.number().min(-180).max(180).optional().nullable(),
  accuracy: z.number().min(0).optional().nullable(),

  checklist: checklistAnswerRecordSchema.optional(),
  damages: z.array(damageMarkerSchema).default([]),
  photos: z.array(photoMetaSchema).default([]),
  signatures: z.array(signatureInputSchema).default([]),
  notes: z.string().max(4000).optional(),

  /** Zusaetzliche Empfaenger, die der FPK vor Ort eintraegt. */
  extraRecipients: z.array(z.string().email()).max(10).default([]),

  payload: z.record(z.string(), z.unknown()).default({}),

  deviceName: z.string().max(120).optional(),
  appVersion: z.string().max(40).optional(),
});
export type ProtocolSyncInput = z.infer<typeof protocolSyncSchema>;

/**
 * Welche Folgevorgaenge ergeben sich aus einem Nutzerwechsel?
 * - Nutzer gibt altes ab UND bekommt neues -> Rueckgabe + Uebergabe
 * - Nutzer bekommt nur ein neues Fahrzeug   -> nur Uebergabe
 * - Nutzer gibt nur ab                      -> nur Rueckgabe
 */
export function followUpTypes(hasReturn: boolean, hasHandover: boolean): ProtocolType[] {
  const out: ProtocolType[] = [];
  if (hasReturn) out.push(ProtocolType.RUECKGABE);
  if (hasHandover) out.push(ProtocolType.UEBERGABE);
  return out;
}
